import axios from '../services/axiosConfig';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Fetches reports for a specific menu
 * @param {number} menuId - The ID of the menu to fetch reports for
 * @param {string} userRoles - The user roles for authorization (e.g., "PowerUser")
 * @param {boolean} adminUser - Whether the user is an admin (true/false)
 * @returns {Promise<Object>} - The response data with reports information
 */
export const getReportsForMenu = async (menuId, userRoles, adminUser) => {
  try {
    const response = await axios.get(
      `${API_URL}/ManageReports/GetReportsMenu`,
      {
        params: {
          menuId,
          userRoles,
          adminUser
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching reports for menu ID ${menuId}:`, error);
    throw error;
  }
};

/**
 * Changes a report's status (activate or deactivate)
 * @param {string} reportId - The ID of the report to change status
 * @param {string} action - The action to perform ('activate' or 'deactivate')
 * @param {number} menuId - The menu ID the report belongs to
 * @param {string} userRoles - The user roles for authorization
 * @param {boolean} adminUser - Whether the user is an admin
 * @param {string} userName - The username of the person making the change
 * @returns {Promise<Object>} - The response data with status change result
 */
export const changeReportStatus = async (reportId, action, menuId, userRoles, adminUser, userName = "System") => {
  try {
    const response = await axios.post(
      `${API_URL}/ManageReports/ChangeReportStatus`,
      {
        reportId: reportId,
        userName: userName,
        action: action,
        menuId: menuId,
        userRoles: userRoles,
        adminUser: String(adminUser)
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error changing report status (${action}) for report ID ${reportId}:`, error);
    throw error;
  }
};

/**
 * Gets details for a specific report
 * @param {number} reportId - The ID of the report to fetch
 * @returns {Promise<Object>} - The response data with report details
 */
export const getReportDetails = async (reportId) => {
  try {
    const response = await axios.get(`${API_URL}/ManageReports/GetReport/${reportId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching report details for ID ${reportId}:`, error);
    throw error;
  }
};

/**
 * Creates a new report
 * @param {Object} reportData - The report data to create
 * @returns {Promise<Object>} - The response data with the created report
 */
export const createReport = async (reportData) => {
  try {
    const response = await axios.post(`${API_URL}/ManageReports/CreateReport`, reportData);
    return response.data;
  } catch (error) {
    console.error('Error creating report:', error);
    throw error;
  }
};

/**
 * Updates an existing report
 * @param {number} reportId - The ID of the report to update
 * @param {Object} reportData - The updated report data
 * @returns {Promise<Object>} - The response data with the updated report
 */
export const updateReport = async (reportId, reportData) => {
  try {
    const response = await axios.put(`${API_URL}/ManageReports/UpdateReport/${reportId}`, reportData);
    return response.data;
  } catch (error) {
    console.error(`Error updating report with ID ${reportId}:`, error);
    throw error;
  }
};

/**
 * Fetches parameter details from the ServiceConfig API
 * @param {string} parameterName - The name of the parameter to fetch details for
 * @returns {Promise<Object>} - The response data with parameter details
 */
export const getParameterDetails = async (parameterName) => {
  try {
    const response = await axios.get(
      `${API_URL}/ServiceConfig/GetParameterDetails`,
      {
        params: {
          parameterName
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching parameter details for ${parameterName}:`, error);
    throw error;
  }
};

